import types from '../../mutation-types';
import axios from 'axios';
import TemplateConverter from '../../../services/TemplateConverter';
import Auth from '../../../api/auth';
import {
  buildEditorJsonHeaders,
  getStoredEditorAccountId,
} from '../../../helper/editorStore';

const state = {
  blocks: [],
  selectedBlockId: null,
  editingBlockId: null,
  isDragging: false,
  isEditing: false,
  isAnyBlockResizing: false,
  draggedBlockType: null,
  previewImage: null,
  history: {
    past: [],
    future: [],
  },
  templates: [],
  layoutMode: '1d',
  blockPositions: {},
  layoutSettings: {
    backgroundColor: '#ffffff',
  },
};

const getCurrentAccountId = (rootState, rootGetters, fallbackAccountId) =>
  fallbackAccountId ||
  rootGetters?.getCurrentAccountId ||
  rootState.accounts.records?.[0]?.id ||
  getStoredEditorAccountId();

const getAuthHeaders = () => {
  if (!Auth.hasAuthCookie()) return {};

  const authData = Auth.getAuthData();

  return {
    'access-token': authData['access-token'],
    'token-type': authData['token-type'],
    client: authData.client,
    expiry: authData.expiry,
    uid: authData.uid,
  };
};

const getJsonHeaders = () =>
  buildEditorJsonHeaders({ authHeaders: getAuthHeaders() });

export const mutations = {
  [types.SET_EDITOR_BLOCKS](_state, blocks) {
    _state.blocks = [...blocks];
  },
  [types.SET_EDITOR_SELECTED_BLOCK](_state, id) {
    _state.selectedBlockId = id;
  },
  [types.SET_EDITOR_DRAGGING](_state, isDragging) {
    _state.isDragging = isDragging;
  },
  [types.SET_EDITOR_EDITING](_state, isEditing) {
    _state.isEditing = isEditing;
  },
  [types.ADD_EDITOR_HISTORY](_state, blocks) {
    _state.history.past.push([...blocks]);
    _state.history.future = [];
  },
  [types.SET_EDITOR_EDITING_BLOCK](_state, id) {
    _state.editingBlockId = id;
  },
  [types.UPDATE_EDITOR_BLOCK](_state, { id, changes }) {
    const updateBlocksRecursively = (blocks) => {
      return blocks.map((block) => {
        if (block.id === id) {
          const updatedBlock = { ...block, ...changes };
          return updatedBlock;
        }

        if (block.type === 'row' && block.content?.columns) {
          return {
            ...block,
            content: {
              ...block.content,
              columns: block.content.columns.map((column) => ({
                ...column,
                content: {
                  ...column.content,
                  blocks: updateBlocksRecursively(column.content.blocks || []),
                },
              })),
            },
          };
        }

        return block;
      });
    };

    const updatedBlocks = updateBlocksRecursively(_state.blocks);
    _state.blocks = updatedBlocks;
  },
  [types.SET_EDITOR_DRAGGED_BLOCK_TYPE](_state, type) {
    _state.draggedBlockType = type;
  },

  // Nuova mutazione per aggiungere un blocco a una colonna specifica
  [types.ADD_BLOCK_TO_COLUMN](_state, { columnId, block }) {
    const addBlock = (blocks) => {
      return blocks.map((blockItem) => {
        if (blockItem.type === 'row' && blockItem.content?.columns) {
          const updatedColumns = blockItem.content.columns.map((column) => {
            if (column.id === columnId) {
              return {
                ...column,
                content: {
                  ...column.content,
                  blocks: [...(column.content.blocks || []), block],
                },
              };
            }
            return column;
          });
          return {
            ...blockItem,
            content: {
              ...blockItem.content,
              columns: updatedColumns,
            },
          };
        }
        return blockItem;
      });
    };

    const updatedBlocks = addBlock(_state.blocks);
    _state.blocks = updatedBlocks;
  },

  [types.DELETE_BLOCK](_state, blockId) {
    const deleteBlockRecursively = (blocks) => {
      return blocks.filter((block) => {
        // Se è un blocco row, cerca nelle colonne
        if (block.type === 'row' && block.content?.columns) {
          return {
            ...block,
            content: {
              ...block.content,
              columns: block.content.columns.map((column) => ({
                ...column,
                content: {
                  ...column.content,
                  blocks: deleteBlockRecursively(column.content.blocks || []),
                },
              })),
            },
          };
        }
        // Filtra via il blocco con l'ID corrispondente
        return block.id !== blockId;
      });
    };

    const updatedBlocks = deleteBlockRecursively(_state.blocks);
    _state.blocks = updatedBlocks;
  },
  [types.SET_EDITOR_TEMPLATES](_state, templates) {
    _state.templates = templates;
  },
  [types.REMOVE_TEMPLATE](_state, templateId) {
    _state.templates = _state.templates.filter((t) => t.id !== templateId);
  },
  SET_BLOCK_RESIZING(_state, value) {
    _state.isAnyBlockResizing = value;
  },
  [types.SET_EDITOR_LAYOUT_MODE](_state, mode) {
    _state.layoutMode = mode;
  },
  [types.SET_BLOCK_POSITION](_state, { blockId, position }) {
    _state.blockPositions[blockId] = position;
  },
  [types.UPDATE_COLUMN](_state, { columnId, updates }) {
    const block = _state.blocks.find((b) =>
      b.content?.columns?.some((col) => col.id === columnId)
    );

    if (block) {
      const columnIndex = block.content.columns.findIndex(
        (col) => col.id === columnId
      );

      if (columnIndex !== -1) {
        block.content.columns[columnIndex] = updates;
      }
    }
  },
  SET_PREVIEW_IMAGE(_state, image) {
    _state.previewImage = image;
  },
  [types.RESET_EDITOR_BLOCKS](_state) {
    _state.blocks = [];
    _state.selectedBlockId = null;
    _state.editingBlockId = null;
    _state.isDragging = false;
    _state.isEditing = false;
    _state.isAnyBlockResizing = false;
    _state.draggedBlockType = null;
    _state.history = {
      past: [],
      future: [],
    };
  },
  [types.UPDATE_LAYOUT_SETTINGS](_state, settings) {
    _state.layoutSettings = {
      ..._state.layoutSettings,
      ...settings,
    };
  },
};

const actions = {
  addBlock({ commit, state: editorState }, payload) {
    const block = payload?.block || payload;
    if (!block) {
      throw new Error('Block non valido');
    }

    const currentBlocks = [...editorState.blocks];

    const newBlock = {
      ...block,
      section: block.section || 'content',
      height: block.height || 200, // Imposta un'altezza predefinita
      width: block.width || '100%', // Imposta una larghezza predefinita
    };

    const updatedBlocks = [...currentBlocks, newBlock];

    commit(types.SET_EDITOR_BLOCKS, updatedBlocks);
    commit(types.ADD_EDITOR_HISTORY, updatedBlocks);

    return newBlock;
  },

  updateBlocks({ commit, state: editorState, dispatch }, blocks) {
    if (blocks.length === editorState.blocks.length + 1) {
      const newBlock = blocks[blocks.length - 1];
      return dispatch('addBlock', {
        type: newBlock.type,
        block: newBlock,
      });
    }

    commit(types.SET_EDITOR_BLOCKS, blocks);
    commit(types.ADD_EDITOR_HISTORY, blocks);
    return blocks;
  },

  selectBlock({ commit }, id) {
    commit(types.SET_EDITOR_SELECTED_BLOCK, id);
  },
  setDragging({ commit }, isDragging) {
    commit(types.SET_EDITOR_DRAGGING, isDragging);
  },
  startEditing({ commit }, id) {
    commit(types.SET_EDITOR_EDITING_BLOCK, id);
    commit(types.SET_EDITOR_EDITING, true);
  },
  stopEditing({ commit }) {
    commit(types.SET_EDITOR_EDITING_BLOCK, null);
    commit(types.SET_EDITOR_EDITING, false);
  },
  updateBlock({ commit }, payload) {
    commit(types.UPDATE_EDITOR_BLOCK, payload);
  },
  setDraggedBlockType({ commit }, type) {
    commit(types.SET_EDITOR_DRAGGED_BLOCK_TYPE, type);
  },
  setEditing({ commit }, { blockId, isEditing }) {
    commit(types.SET_EDITOR_EDITING, isEditing);
    commit(types.SET_EDITOR_EDITING_BLOCK, blockId);
  },

  // Nuova azione per aggiungere un blocco a una colonna specifica
  addBlockToColumn({ commit }, { columnId, block, parentId }) {
    // Aggiorna la struttura gerarchica
    const updatedBlock = {
      ...block,
      parentId,
      columnId,
    };

    commit(types.ADD_BLOCK, updatedBlock);
    return updatedBlock;
  },

  deleteBlock({ commit, state: editorState }, blockId) {
    commit(types.DELETE_BLOCK, blockId);
    commit(types.ADD_EDITOR_HISTORY, editorState.blocks);

    return true;
  },

  // ... x il Salvataggio del Template

  async saveTemplate({ rootState, rootGetters }, templateData) {
    const accountId = getCurrentAccountId(
      rootState,
      rootGetters,
      templateData.account_id
    );

    if (!accountId) {
      throw new Error('Account ID non trovato');
    }

    const response = await axios.post(
      `/api/v1/accounts/${accountId}/editor_templates`,
      {
        editor_template: {
          ...templateData,
          account_id: accountId,
        },
      },
      {
        headers: getJsonHeaders(),
      }
    );

    return response.data;
  },

  async fetchTemplates({ rootState, rootGetters, commit }) {
    try {
      const accountId = getCurrentAccountId(rootState, rootGetters);

      if (!accountId) {
        throw new Error('Account ID non trovato');
      }

      const response = await axios.get(
        `/api/v1/accounts/${accountId}/editor_templates`,
        { headers: getAuthHeaders() }
      );
      commit(types.SET_EDITOR_TEMPLATES, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },

  async deleteTemplate({ rootState, rootGetters, commit }, templateId) {
    try {
      const accountId = getCurrentAccountId(rootState, rootGetters);

      if (!accountId) {
        throw new Error('Account ID non trovato');
      }

      await axios.delete(
        `/api/v1/accounts/${accountId}/editor_templates/${templateId}`,
        {
          headers: getJsonHeaders(),
        }
      );

      commit(types.REMOVE_TEMPLATE, templateId);
    } catch (error) {
      throw new Error('Impossibile eliminare il template');
    }
  },

  async duplicateTemplate({ rootState, rootGetters, dispatch }, templateId) {
    const accountId = getCurrentAccountId(rootState, rootGetters);

    if (!accountId) {
      throw new Error('Account ID non trovato');
    }

    await axios.post(
      `/api/v1/accounts/${accountId}/editor_templates/${templateId}/duplicate`,
      {},
      {
        headers: getJsonHeaders(),
      }
    );

    return dispatch('fetchTemplates');
  },

  async loadTemplate({ rootState, rootGetters, commit }, templateId) {
    const accountId = getCurrentAccountId(rootState, rootGetters);

    if (!accountId) {
      throw new Error('Account ID non trovato');
    }

    const response = await axios.get(
      `/api/v1/accounts/${accountId}/editor_templates/${templateId}`,
      {
        headers: getJsonHeaders(),
      }
    );

    commit(types.SET_EDITOR_BLOCKS, response.data.content);
    return response.data;
  },

  setBlockResizing({ commit }, value) {
    commit('SET_BLOCK_RESIZING', value);
  },

  async handleInsert({ state: editorState }, blocks) {
    const cleanBlocks = JSON.parse(JSON.stringify(blocks));

    // Convertiamo i blocchi in formato email usando TemplateConverter
    const convertedBlocks = TemplateConverter.toEmailFormat(
      cleanBlocks,
      editorState.layoutSettings
    );

    return {
      blocks: cleanBlocks,
      convertedBlocks,
    };
  },

  setPreviewImage({ commit }, image) {
    if (!image) {
      throw new Error('Immagine non valida');
    }
    commit('SET_PREVIEW_IMAGE', image);
    return true;
  },
  resetBlocks({ commit }) {
    commit(types.RESET_EDITOR_BLOCKS);
  },
  updateLayoutSettings({ commit }, settings) {
    commit(types.UPDATE_LAYOUT_SETTINGS, settings);
  },
};

const getters = {
  blocks: (editorState) => editorState.blocks,
  selectedBlockId: (editorState) => editorState.selectedBlockId,
  editingBlockId: (editorState) => editorState.editingBlockId,
  selectedBlock: (editorState) =>
    editorState.blocks.find(
      (block) => block.id === editorState.selectedBlockId
    ),
  contentBlocks: (editorState) => {
    return editorState.blocks.filter((block) => {
      return block.section === 'content' || block.type === 'row';
    });
  },
  canUndo: (editorState) => editorState.history.past.length > 0,
  canRedo: (editorState) => editorState.history.future.length > 0,
  templates: (editorState) => editorState.templates,
  isEditing: (editorState) => editorState.isEditing,
  isAnyBlockResizing: (editorState) => editorState.isAnyBlockResizing,
  layoutMode: (editorState) => editorState.layoutMode,
  blockPosition: (editorState) => (blockId) =>
    editorState.blockPositions[blockId] || null,
  previewImage: (editorState) => editorState.previewImage,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
